import styled, { css, keyframes } from 'styled-components'

import { theme } from 'styles/theme'

import { thinStroke } from '../icon-stroke'

const { colors, fontSizes, fontWeights, lineHeights, media, radii, space } = theme

type CardProps = {
  readonly $isVisible: boolean
  readonly $order: number
}

const revealCard = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const Section = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: ${space['spacing-xxxl']} 0;
  background-color: #f4f6fb;

  @media (${media.desktop}) {
    min-height: 985px;
    padding: ${space['spacing-giga']} 0;
  }
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - ${space['spacing-xxl']});
  max-width: 1180px;
  margin: 0 auto;

  .ds-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${space['spacing-sm']};
    width: 100%;
    height: ${space['spacing-mega']};
    margin-top: ${space['spacing-xl']};
    padding: 0 ${space['spacing-sm']};
    border-radius: ${radii['corner-radius-pill']};
    background-color: ${colors['blue-background-strong-default']};
    transition: background-color 180ms ease;

    &:hover {
      background-color: ${colors['blue-background-strong-hover']};
    }

    &:focus-visible {
      outline: 2px solid ${colors['neutral-text-default']};
      outline-offset: 3px;
    }
  }

  @media (${media.desktop}) {
    .ds-button {
      width: 230px;
      margin: ${space['spacing-md']} auto 0;
    }
  }
`

export const Heading = styled.div`
  h2 {
    margin: 0;
    font-size: ${fontSizes['font-size-mobile-heading-2']};
    line-height: 1.2;
    letter-spacing: -0.72px;
    text-align: left;
  }

  @media (${media.desktop}) {
    h2 {
      font-size: ${fontSizes['font-size-heading-2']};
      line-height: ${lineHeights['font-line-height-sm']};
      letter-spacing: -0.96px;
      text-align: center;
    }
  }
`

export const DesktopBreak = styled.br`
  display: none;

  @media (${media.desktop}) {
    display: block;
  }
`

export const Highlight = styled.span`
  color: ${colors['blue-background-strong-default']};
  font-weight: 600;

  @media (${media.desktop}) {
    color: ${colors['pink-text-contrast']};
  }
`

export const Intro = styled.div`
  margin-top: 30px;

  p {
    width: 100%;
    margin: 0;
    font-size: 17px;
    line-height: ${lineHeights['font-line-height-lg']};
  }

  @media (${media.desktop}) {
    width: 800px;
    max-width: 100%;
    margin: ${space['spacing-xl']} auto 0;

    p {
      font-size: ${fontSizes['font-size-body-small']};
    }
  }
`

export const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 20px;

  @media (${media.desktop}) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: 252px;
    gap: ${space['spacing-md']};
    margin-top: ${space['spacing-xl']};
  }
`

export const Card = styled.article<CardProps>`
  position: sticky;
  top: ${({ $order }) => $order * 5 + 1.5}rem;
  z-index: ${({ $order }) => $order + 1};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-sm']};
  width: 100%;
  height: 200px;
  padding: ${space['spacing-md']};
  overflow: hidden;
  border-top: 1px solid #e6e8eb;
  border-radius: 30px 30px 0 0;
  background-color: ${colors['neutral-background-default']};
  ${({ $isVisible, $order }) =>
    $isVisible &&
    css`
      animation: ${revealCard} 420ms ease backwards;
      animation-delay: ${$order * 70}ms;
    `}

  &:not(:first-child) {
    margin-top: -${space['spacing-md']};
  }

  &:last-child {
    border-radius: 30px;
  }

  @media (${media.desktop}) {
    position: static;
    top: auto;
    z-index: auto;
    gap: 0;
    height: 252px;
    padding: 25px;
    border: 1px solid ${colors['neutral-border-weak']};
    border-radius: 18px;
    transition:
      border-color 180ms ease,
      box-shadow 180ms ease,
      transform 180ms ease;

    &:not(:first-child) {
      margin-top: 0;
    }

    &:last-child {
      border: 1px solid ${colors['neutral-border-weak']};
      border-radius: 18px;
    }
  }

  @media (hover: hover) and (${media.desktop}) {
    &:hover {
      border-color: ${colors['blue-border-default']};
      box-shadow: 0 12px 24px rgb(0 40 75 / 12%);
      transform: translateY(-4px);
    }

    &:hover svg,
    &:hover img {
      transform: scale(1.06);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;

    &:hover {
      transform: none;
    }
  }
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['spacing-sm']};

  @media (${media.desktop}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${space['spacing-md']};
  }
`

export const Icon = styled.div`
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  ${thinStroke}

  svg,
  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    transition: transform 180ms ease;
  }

  @media (${media.desktop}) {
    flex-basis: 64px;
    width: 64px;
    height: 64px;

    svg,
    img {
      width: 64px;
      height: 64px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    svg,
    img {
      transition: none;
    }
  }
`

export const CardTitle = styled.div`
  h3 {
    margin: 0;
    font-size: ${fontSizes['font-size-mobile-heading-5']};
    font-weight: ${fontWeights['font-weight-medium']};
    line-height: ${lineHeights['font-line-height-lg']};
  }

  @media (${media.desktop}) {
    h3 {
      font-size: ${fontSizes['font-size-mobile-heading-5']};
      font-weight: 600;
      line-height: normal;
      letter-spacing: -0.18px;
    }
  }
`

export const Description = styled.div`
  p {
    margin: 0;
    font-size: 17px;
    line-height: ${lineHeights['font-line-height-lg']};
  }

  @media (${media.desktop}) {
    margin-top: ${space['spacing-xs']};

    p {
      color: ${colors['neutral-text-weaker']};
      font-size: ${fontSizes['font-size-body-1']};
      line-height: normal;
    }
  }
`
